import { useMutation } from '@tanstack/react-query';

import { useAuth } from '@/app/providers/auth/authContext';
import type { GameState, Side } from '@/core/engine/x01/types';
import { deriveScore } from '@/core/engine/x01/game';
import {
  uidSchema,
  type EntryMode,
  type Match,
  type RecordedTurn,
  type Team,
  type TeamId,
} from '@/core/schemas';
import { describeError } from '@/features/team/describeError';
import { sideLabel } from '@/features/matches/matchPresentation';
import {
  confirmResult,
  rejectResult,
  saveTurns,
  startCounter,
} from '@/infrastructure/services/counterService';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/utils/cn';

import { CounterStartForm } from './CounterStartForm';
import { DartEntry } from './DartEntry';
import { SumKeypad } from './SumKeypad';

interface CounterSectionProps {
  match: Match;
  game: GameState | null;
  startScore: number;
  doubleOut: boolean;
  lookup: (teamId: TeamId) => Team | undefined;
  /** The viewer's own side, or null when they are not a participant. */
  ownSide: Side | null;
}

export function CounterSection({
  match,
  game,
  startScore,
  doubleOut,
  lookup,
  ownSide,
}: CounterSectionProps) {
  const { session } = useAuth();
  const actorUid = session.status === 'authenticated' ? uidSchema.parse(session.account.uid) : null;
  const canScore = ownSide !== null; // one device at the board: either captain scores

  const start = useMutation({
    mutationFn: (input: { entryMode: EntryMode; startingSide: Side }) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return startCounter({ match, startScore, doubleOut, ...input, actorUid });
    },
  });

  const save = useMutation({
    mutationFn: (turns: readonly RecordedTurn[]) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return saveTurns({ match, turns, actorUid });
    },
  });

  const confirm = useMutation({
    mutationFn: () => {
      if (actorUid === null || ownSide === null || game === null) throw new Error('Nicht möglich');
      return confirmResult({ match, side: ownSide, game, actorUid });
    },
  });

  const reject = useMutation({
    mutationFn: () => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return rejectResult({ match, actorUid });
    },
  });

  const error =
    describeError(start.error) ??
    describeError(save.error) ??
    describeError(confirm.error) ??
    describeError(reject.error);

  // Not started yet.
  if (game === null || match.counter === null) {
    if (!canScore) {
      return <Alert tone="info">Das Spiel wird am Board gestartet, sobald es losgeht.</Alert>;
    }
    return (
      <div className="flex flex-col gap-3">
        {error !== null && <Alert tone="danger">{error}</Alert>}
        <CounterStartForm
          match={match}
          lookup={lookup}
          startScore={startScore}
          doubleOut={doubleOut}
          isSubmitting={start.isPending}
          onStart={(entryMode, startingSide) => start.mutate({ entryMode, startingSide })}
        />
      </div>
    );
  }

  const counter = match.counter;
  const decided = game.winner !== null;
  const toThrow = game.currentLeg.toThrow;

  const submitTurn = (points: number, finishedOnDouble: boolean, darts?: RecordedTurn['darts']) => {
    const turn: RecordedTurn = {
      points,
      finishedOnDouble,
      ...(darts !== undefined && darts.length > 0 ? { darts } : {}),
    };
    save.mutate([...counter.turns, turn]);
  };

  return (
    <div className="flex flex-col gap-4">
      {error !== null && <Alert tone="danger">{error}</Alert>}

      <Scoreboard match={match} game={game} lookup={lookup} />

      {!decided && canScore && (
        <>
          {counter.entryMode === 'sum' ? (
            <SumKeypad
              remaining={game.currentLeg.remaining[toThrow]}
              disabled={save.isPending}
              onSubmit={(points) =>
                submitTurn(points, sumFinishesOnDouble(game, points, doubleOut))
              }
            />
          ) : (
            <DartEntry
              remaining={game.currentLeg.remaining[toThrow]}
              disabled={save.isPending}
              onSubmit={(points, finishedOnDouble, darts) =>
                submitTurn(points, finishedOnDouble, darts)
              }
            />
          )}

          {counter.turns.length > 0 && (
            <Button
              variant="ghost"
              size="md"
              className="self-center"
              disabled={save.isPending}
              onClick={() => save.mutate(counter.turns.slice(0, -1))}
            >
              Letzte Aufnahme zurücknehmen
            </Button>
          )}
        </>
      )}

      {!decided && !canScore && (
        <Alert tone="info">
          {sideLabel(game.currentLeg.toThrow === 'home' ? match.home : match.away, lookup)} ist am
          Wurf. Die Anzeige aktualisiert sich automatisch.
        </Alert>
      )}

      {decided && (
        <ConfirmationPanel
          match={match}
          game={game}
          lookup={lookup}
          ownSide={ownSide}
          isConfirming={confirm.isPending}
          isRejecting={reject.isPending}
          onConfirm={() => confirm.mutate()}
          onReject={() => reject.mutate()}
        />
      )}
    </div>
  );
}

/**
 * In sum mode a checkout is trusted: reaching exactly the remaining score is
 * treated as finishing on a double when double-out is on.
 */
function sumFinishesOnDouble(game: GameState, points: number, doubleOut: boolean): boolean {
  if (!doubleOut) {
    return false;
  }
  const side = game.currentLeg.toThrow;
  return points === game.currentLeg.remaining[side];
}

function Scoreboard({
  match,
  game,
  lookup,
}: {
  match: Match;
  game: GameState;
  lookup: (teamId: TeamId) => Team | undefined;
}) {
  const score = deriveScore(game);

  return (
    <div className="grid grid-cols-2 gap-3">
      {(['home', 'away'] as const).map((side) => {
        const active = game.winner === null && game.currentLeg.toThrow === side;
        const isWinner = game.winner === side;
        return (
          <div
            key={side}
            className={cn(
              'rounded-2xl border p-5 text-center',
              active ? 'border-brand bg-brand-subtle' : 'border-line bg-surface',
              isWinner && 'border-success bg-success-subtle',
            )}
          >
            <p className="truncate text-sm font-medium">
              {sideLabel(side === 'home' ? match.home : match.away, lookup)}
            </p>
            <p className="mt-2 font-mono text-5xl font-black tabular-nums">
              {game.currentLeg.remaining[side]}
            </p>
            <p className="text-fg-muted mt-2 text-xs">
              {game.usesSets ? `${score.sets?.[side] ?? 0} Sätze · ` : ''}
              {score.legs[side]} Legs
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ConfirmationPanel({
  match,
  game,
  lookup,
  ownSide,
  isConfirming,
  isRejecting,
  onConfirm,
  onReject,
}: {
  match: Match;
  game: GameState;
  lookup: (teamId: TeamId) => Team | undefined;
  ownSide: Side | null;
  isConfirming: boolean;
  isRejecting: boolean;
  onConfirm: () => void;
  onReject: () => void;
}) {
  const winnerSide = game.winner;
  const score = deriveScore(game);
  const alreadyConfirmed = ownSide !== null && match.confirmations[ownSide] !== undefined;

  return (
    <section className="border-brand/40 bg-brand-subtle flex flex-col gap-4 rounded-xl border p-5">
      <div>
        <p className="text-fg-subtle text-xs tracking-widest uppercase">Spiel entschieden</p>
        <p className="mt-1 text-2xl font-bold">
          {winnerSide === null
            ? 'Unentschieden'
            : `Sieger: ${sideLabel(winnerSide === 'home' ? match.home : match.away, lookup)}`}
        </p>
        <p className="text-fg-muted mt-1 text-sm">
          {score.legs.home}:{score.legs.away} Legs
          {score.sets !== null ? ` · ${score.sets.home}:${score.sets.away} Sätze` : ''}
        </p>
      </div>

      <ul className="text-sm">
        {(['home', 'away'] as const).map((side) => (
          <li
            key={side}
            className="flex items-center justify-between border-t border-white/10 py-1"
          >
            <span>{sideLabel(side === 'home' ? match.home : match.away, lookup)}</span>
            <span
              className={
                match.confirmations[side] !== undefined ? 'text-success' : 'text-fg-subtle'
              }
            >
              {match.confirmations[side] !== undefined ? 'bestätigt' : 'offen'}
            </span>
          </li>
        ))}
      </ul>

      {ownSide === null ? (
        <Alert tone="info">Beide Teamleiter bestätigen das Ergebnis auf ihren Geräten.</Alert>
      ) : alreadyConfirmed ? (
        <Alert tone="success">
          Deine Bestätigung liegt vor. Sobald die Gegenseite bestätigt, gilt das Spiel als beendet.
        </Alert>
      ) : (
        <div className="flex flex-wrap gap-3">
          <Button onClick={onConfirm} isLoading={isConfirming}>
            Ergebnis bestätigen
          </Button>
          <Button variant="danger" onClick={onReject} isLoading={isRejecting}>
            Ergebnis anzweifeln
          </Button>
        </div>
      )}
    </section>
  );
}
